import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Clock, Coffee, Wrench, ShieldAlert, Pencil, Ban, RotateCcw, Plus, Lock } from 'lucide-react';
import TimeAllocation from './TimeAllocation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAG_LABEL = {
  weekly_paid_double_deducts_break: 'Weekly "Paid" double-deducts break',
  payroll_total_differs: 'Payroll differs', weekly_total_differs: 'Weekly total differs',
};
const prettyFlag = (f) => {
  if (FLAG_LABEL[f]) return FLAG_LABEL[f];
  const [k, n] = f.split(':');
  const map = { open_entries: 'Open', auto_closed: 'Auto-closed', fallback: 'Fallback', voided_excluded: 'Voided excluded', stored_duration_stale: 'Stored stale', timezone_day_shift: 'TZ shift' };
  return `${map[k] || k}${n ? ` (${n})` : ''}`;
};

const VOID_REASONS = [
  { v: 'duplicate_clock_entry', l: 'Duplicate clock entry' },
  { v: 'forgot_clock_out', l: 'Employee forgot to clock out' },
  { v: 'forgot_clock_in', l: 'Employee forgot to clock in' },
  { v: 'incorrect_clock_in', l: 'Incorrect clock-in time' },
  { v: 'incorrect_clock_out', l: 'Incorrect clock-out time' },
  { v: 'wrong_job_shift', l: 'Wrong job/shift' },
  { v: 'break_correction', l: 'Break correction' },
  { v: 'auto_close_correction', l: 'Auto-close correction' },
  { v: 'approved_edit_request', l: 'Approved employee edit request' },
  { v: 'other', l: 'Other' },
];

const StatusChip = ({ e }) => {
  if (e.voided) return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/15 border border-red-500/50 text-red-300">VOIDED</span>;
  if (e.is_open) return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/15 border border-blue-500/50 text-blue-300">OPEN</span>;
  if (e.auto_closed) return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 border border-amber-500/50 text-amber-200">AUTO-CLOSED</span>;
  return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#262626] border border-[#404040] text-[#C9C9C9]">CLOSED</span>;
};

const TimesheetDayDetailDrawer = ({
  open, onOpenChange, employeeId, employeeName, date,
  canEdit = false, reloadToken = 0, onEditEntry, onAddEntry, onChanged,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allocation, setAllocation] = useState(null);
  // Void/restore confirm dialog
  const [voidCtx, setVoidCtx] = useState({ open: false, entry: null, mode: 'void' });
  const [voidReason, setVoidReason] = useState('');
  const [voidNote, setVoidNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchDay = () => {
    if (!employeeId || !date) return;
    setLoading(true);
    axios.get(`${API}/admin/timesheets/${employeeId}/day?date=${date}`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  };

  const fetchAllocation = () => {
    if (!employeeId || !date) return;
    axios.get(`${API}/admin/timesheets/${employeeId}/allocation?date=${date}`, { withCredentials: true })
      .then(({ data }) => setAllocation(data))
      .catch(() => setAllocation(null));
  };

  useEffect(() => {
    if (!open) return;
    setData(null);
    setAllocation(null);
    fetchDay();
    fetchAllocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employeeId, date, reloadToken]);

  const reconciles = data && Math.abs((data.centralized_paid_hours || 0) - (data.stored_duration_sum || 0)) < 0.02;

  const toSheetEntry = (e) => ({
    id: e.id, user_id: employeeId, user_name: employeeName,
    clock_in: e.clock_in, clock_out: e.clock_out, break_hours: e.break_hours,
    appointment_label: e.appointment_label || '', auto_closed: e.auto_closed, fallback_clockin: e.fallback_clockin,
  });

  const openVoid = (entry, mode) => {
    setVoidCtx({ open: true, entry, mode });
    setVoidReason(''); setVoidNote(''); setPreview(null);
    if (mode === 'void') {
      axios.post(`${API}/admin/timeclock/preview`, { user_id: employeeId, date, action: 'void', entry_id: entry.id }, { withCredentials: true })
        .then(({ data }) => setPreview(data)).catch(() => setPreview(null));
    }
  };

  const confirmVoid = async () => {
    const { entry, mode } = voidCtx;
    if (mode === 'void') {
      if (!voidReason) { toast.error('Select a correction reason'); return; }
      if (voidReason === 'other' && !voidNote.trim()) { toast.error('Notes required for "Other"'); return; }
    } else if (!voidNote.trim()) { toast.error('Reason required to restore'); return; }
    setBusy(true);
    try {
      if (mode === 'void') {
        await axios.post(`${API}/admin/timeclock/${entry.id}/void`, { reason: voidReason, reason_note: voidNote }, { withCredentials: true });
        toast.success('Entry voided — excluded from totals');
      } else {
        await axios.post(`${API}/admin/timeclock/${entry.id}/restore`, { reason: voidNote }, { withCredentials: true });
        toast.success('Entry restored');
      }
      setVoidCtx({ open: false, entry: null, mode: 'void' });
      onChanged?.();
      fetchDay();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent data-testid="ts-day-drawer" className="bg-[#141414] border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#D4AF37]" /> {employeeName || 'Employee'} — {date}
            </SheetTitle>
          </SheetHeader>
          <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
            <p className="text-[10px] text-[#737373] uppercase tracking-wider">{canEdit ? 'Admin correction mode' : 'Read-only diagnostic'} · America/New_York</p>
            {canEdit ? (
              <span data-testid="ts-day-admin-mode" className="text-[10px] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-2 py-0.5 rounded font-bold">Admin Correction Mode</span>
            ) : (
              <span className="text-[10px] text-[#737373] flex items-center gap-1"><Lock className="w-3 h-3" /> View only</span>
            )}
          </div>

          {loading && <div className="flex items-center gap-2 text-[#C9C9C9] mt-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {data?.error && <p className="text-red-300 mt-6">Could not load day detail.</p>}

          {data && !data.error && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5" data-testid="ts-day-paid">
                  <p className="text-[10px] uppercase tracking-wider text-[#737373]">Paid (recomputed)</p>
                  <p className="text-lg font-bold text-[#D4AF37]">{(data.centralized_paid_hours || 0).toFixed(2)}h</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-[#737373]">Worked</p>
                  <p className="text-lg font-bold text-white">{(data.centralized_worked_hours || 0).toFixed(2)}h</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-[#737373]">Break (unpaid)</p>
                  <p className="text-lg font-bold text-white">{(data.centralized_break_hours || 0).toFixed(2)}h</p>
                </div>
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-[#737373]">Stored sum (current)</p>
                  <p className={`text-lg font-bold ${reconciles ? 'text-white' : 'text-red-300'}`}>{(data.stored_duration_sum || 0).toFixed(2)}h</p>
                </div>
              </div>
              {!reconciles && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-md p-2.5 text-red-200 text-xs flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4" /> Recomputed paid hours differ from the stored sum current views show — review the flagged entries below.
                </div>
              )}

              <div className="flex items-center justify-between">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Contributing entries ({data.entries?.length || 0})</p>
                {canEdit && (
                  <Button data-testid="ts-day-add-entry" onClick={onAddEntry} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-8">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Time Entry
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {(data.entries || []).map((e) => (
                  <div key={e.id} data-testid={`ts-day-entry-${e.id}`} className={`border rounded-md p-3 ${e.voided ? 'border-red-500/40 bg-red-500/[0.04]' : 'border-[#262626] bg-[#0A0A0A]'}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-white font-medium text-sm">{e.clock_in_et || '—'} → {e.clock_out_et || (e.is_open ? 'still open' : '—')}</span>
                      <StatusChip e={e} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 mt-2 text-xs">
                      <div><span className="text-[#737373]">Worked</span><br /><span className="text-white font-bold">{e.worked_gross_hours.toFixed(2)}h</span></div>
                      <div><span className="text-[#737373]">Break</span><br /><span className="text-white font-bold">{e.break_hours.toFixed(2)}h</span></div>
                      <div><span className="text-[#737373]">Paid</span><br /><span className="text-[#D4AF37] font-bold">{e.paid_net_hours.toFixed(2)}h</span></div>
                      <div><span className="text-[#737373]">Stored</span><br /><span className={`font-bold ${e.stored_matches_recompute ? 'text-white' : 'text-red-300'}`}>{e.stored_duration_hours.toFixed(2)}h</span></div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-[#A3A3A3]">
                      <span className="flex items-center gap-1"><Wrench className="w-3 h-3" /> {e.appointment_label || 'General time'}</span>
                      {e.fallback_clockin && <span className="text-amber-300">Fallback: {e.fallback_reason || 'no reason'}</span>}
                      {e.auto_closed && <span className="text-amber-300">Auto-closed: {e.auto_close_reason || 'no clockout'}</span>}
                      {(e.breaks || []).length > 0 && <span className="flex items-center gap-1"><Coffee className="w-3 h-3" /> {e.breaks.length} break event(s)</span>}
                      {e.corrected && <span className="text-blue-300">Corrected by {e.last_edited_by_name || 'admin'}</span>}
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#262626]">
                        {!e.voided ? (
                          <>
                            <Button data-testid={`ts-entry-edit-${e.id}`} onClick={() => onEditEntry?.(toSheetEntry(e))} size="sm" variant="outline" className="h-7 border-[#404040] text-white hover:bg-[#262626] text-xs">
                              <Pencil className="w-3 h-3 mr-1" /> Edit Entry
                            </Button>
                            <Button data-testid={`ts-entry-void-${e.id}`} onClick={() => openVoid(e, 'void')} size="sm" variant="outline" className="h-7 border-red-500/50 text-red-300 hover:bg-red-500/10 text-xs">
                              <Ban className="w-3 h-3 mr-1" /> Void Entry
                            </Button>
                          </>
                        ) : (
                          <Button data-testid={`ts-entry-restore-${e.id}`} onClick={() => openVoid(e, 'restore')} size="sm" variant="outline" className="h-7 border-green-500/50 text-green-300 hover:bg-green-500/10 text-xs">
                            <RotateCcw className="w-3 h-3 mr-1" /> Restore
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="pt-1 border-t border-[#262626]" data-testid="ts-day-allocation">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2">Where Their Hours Went</p>
                {allocation ? (
                  <TimeAllocation
                    allocation={allocation}
                    userId={employeeId}
                    date={date}
                    adminMode={canEdit}
                    onChanged={() => { fetchAllocation(); fetchDay(); onChanged?.(); }}
                  />
                ) : (
                  <p className="text-[#737373] text-xs">No allocation data for this day.</p>
                )}
              </div>

              {(data.edit_requests || []).length > 0 && (
                <div>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2">Edit requests ({data.edit_requests.length})</p>
                  <div className="space-y-1.5">
                    {data.edit_requests.map((r) => (
                      <div key={r.id} className="bg-[#0A0A0A] border border-[#262626] rounded p-2 text-xs">
                        <span className={`font-bold ${r.status === 'pending' ? 'text-amber-300' : r.status === 'approved' ? 'text-green-300' : 'text-red-300'}`}>{(r.status || '').toUpperCase()}</span>
                        <span className="text-[#C9C9C9]"> — {r.reason || 'no reason'}</span>
                        {r.decline_reason && <span className="text-red-300"> · Declined: {r.decline_reason}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(data.audit || []).length > 0 && (
                <div data-testid="ts-day-audit">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2">Correction audit ({data.audit.length})</p>
                  <div className="space-y-1.5">
                    {data.audit.map((a) => (
                      <div key={a.id} className="bg-[#0A0A0A] border border-[#262626] rounded p-2 text-xs text-[#C9C9C9]">
                        <span className="text-white font-medium">{a.edited_by_name}</span> · <span className="uppercase text-[#D4AF37]">{a.action}</span> · {a.reason}
                        <span className="text-[#737373]"> ({(a.changes || []).map((c) => c.field).join(', ')})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Void / Restore confirm + preview */}
      <Dialog open={voidCtx.open} onOpenChange={(o) => setVoidCtx((s) => ({ ...s, open: o }))}>
        <DialogContent data-testid="ts-void-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {voidCtx.mode === 'void' ? <><Ban className="w-5 h-5 text-red-300" /> Void Entry</> : <><RotateCcw className="w-5 h-5 text-green-300" /> Restore Entry</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            {voidCtx.entry && (
              <p className="text-sm text-[#C9C9C9]">{voidCtx.entry.clock_in_et} → {voidCtx.entry.clock_out_et || 'open'} · {voidCtx.entry.paid_net_hours?.toFixed(2)}h paid</p>
            )}
            {voidCtx.mode === 'void' && preview && (
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-xs space-y-1" data-testid="ts-void-preview">
                <div className="flex justify-between"><span className="text-[#737373]">Day paid</span><span className="text-white">{preview.current_day_paid.toFixed(2)}h → <b className="text-[#D4AF37]">{preview.new_day_paid.toFixed(2)}h</b></span></div>
                <div className="flex justify-between"><span className="text-[#737373]">Week paid</span><span className="text-white">{preview.current_week_paid.toFixed(2)}h → <b className="text-[#D4AF37]">{preview.new_week_paid.toFixed(2)}h</b></span></div>
                <div className="flex justify-between"><span className="text-[#737373]">Difference</span><span className="text-red-300 font-bold">{preview.day_difference.toFixed(2)}h</span></div>
              </div>
            )}
            {voidCtx.mode === 'void' && (
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Correction Reason *</label>
                <Select value={voidReason} onValueChange={setVoidReason}>
                  <SelectTrigger data-testid="ts-void-reason" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    {VOID_REASONS.map((r) => <SelectItem key={r.v} value={r.v} className="text-white">{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">{voidCtx.mode === 'void' ? (voidReason === 'other' ? 'Notes *' : 'Notes (optional)') : 'Reason *'}</label>
              <Textarea data-testid="ts-void-note" rows={2} value={voidNote} onChange={(e) => setVoidNote(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button data-testid="ts-void-confirm" onClick={confirmVoid} disabled={busy} className={`flex-1 h-10 font-bold ${voidCtx.mode === 'void' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (voidCtx.mode === 'void' ? 'Confirm Void' : 'Confirm Restore')}
              </Button>
              <Button onClick={() => setVoidCtx({ open: false, entry: null, mode: 'void' })} variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-10">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimesheetDayDetailDrawer;
